<template>
  <ModalDialog
    header-text="Kosher Certificates"
    :open="open"
    @close-modal="closeModal"
  >
    <template v-slot:content>
      <div class="print-modal-content loc-popup-box" style="display: block">
        <header data-cy="content-header">{{ labelType }}</header>
        <div v-show="!pickupNoticeShown">
          <div
            v-if="!options?.isLinkOption"
            class="loc-form-container"
            data-cy="loc-form-container"
          >
            <div class="" id="popuprow-inhouse">
              <label data-cy="letter-header-label">Letter header</label>
              <DropDownList
                class-name="letter-header-dropdown modal-dropdown"
                :data-items="options?.letterHeaderOptions"
                v-model="selectedHeader"
                :popup-settings="{ className: 'modal-popup' }"
                text-field="text"
                data-cy="letter-header-dropdown"
              />
              <div v-if="options?.sortOrderOptions" class="sort-container">
                <label data-cy="sort-label">Sort</label>
                <DropDownList
                  class-name="sort-dropdown modal-dropdown"
                  :data-items="options?.sortOrderOptions"
                  v-model="selectedSortOrder"
                  :popup-settings="{ className: 'modal-popup' }"
                  text-field="text"
                  data-cy="sort-dropdown"
                />
              </div>
              <label data-cy="print-label">Print options</label>
              <DropDownList
                class-name="print-dropdown modal-dropdown"
                :data-items="options?.printOptions"
                v-model="selectedPrintOption"
                :popup-settings="{ className: 'modal-popup' }"
                text-field="text"
                data-cy="print-dropdown"
              />
              <div
                v-if="selectedPrintOption?.showFileNameFormat"
                class="file-name-format-container"
                data-cy="file-name-format-container"
              >
                <label data-cy="name-constructor-label">File Name Format</label>
                <input
                  type="text"
                  v-model="fileName"
                  class="name-constructor"
                />
                <span
                  v-if="!isCustomNameValid"
                  class="name-constructor-error"
                  >{{ invalidCustomNameText }}</span
                >
                <div
                  class="name-constructor__builders"
                  data-cy="name-constructor-builders"
                >
                  <span
                    v-for="option in fileNameBuilderOptions"
                    :key="option.value"
                    @click="fileNameLabelClicked(option.value)"
                    class="builder"
                    data-cy="constructor-builder"
                    >{{ option.text }}</span
                  >
                </div>
              </div>
            </div>
            <div
              v-if="selectedPrintOption?.showFolderName"
              class="folder-name-container"
              data-cy="folder-name-container"
            >
              <label data-cy="folder-name-label">Folder Name</label>
              <input v-model="folderName" type="text" />
            </div>
            <LoadingButton
              type="button"
              theme-color="primary"
              @click="downloadClicked"
              data-cy="download-button"
              :loading="locPrinting"
              :disabled="
                selectedPrintOption?.showFileNameFormat && !isCustomNameValid
              "
              >Download</LoadingButton
            >
            <input
              id="defaultPrintOption"
              class="print-option-checkbox"
              type="checkbox"
              v-model="setDefaultOptionChecked"
              data-cy="default-print-option-checkbox"
            /><label
              for="defaultPrintOption"
              data-cy="default-print-option-label"
              >Set as my default print option</label
            >
          </div>
          <div v-else class="loc-form-container" data-cy="loc-form-container">
            <ul class="loc-popup-list">
              <li
                v-for="item in (options?.letterHeaderOptions as ILinkLocHeaderOption[])"
                :key="item.value"
              >
                <FontAwesomeIcon
                  class="option-icon"
                  icon="fa-solid fa-file-pdf"
                  data-cy="loc-popup-list-item-icon"
                />
                <LoadingButton
                  type="button"
                  theme-color="primary"
                  @click="linkOptionClicked(item)"
                  :loading="locPrinting && (item.downloading || false)"
                  :disabled="locPrinting && !item.downloading"
                  data-cy="loc-popup-list-item"
                  >{{ item.text }}</LoadingButton
                >
              </li>
            </ul>
          </div>
        </div>
        <div
          v-if="pickupNoticeShown"
          class="loc-pickup-notice"
          data-cy="pickup-notice"
        >
          Certificates available shortly in the<br />
          Kosher Certificates Pickup area of your OUDirect Home page.<br /><br />
          <Button
            theme-color="primary"
            @click="pickupNoticeShown = false"
            data-cy="notice-button"
          >
            Click here for another download with the same parameters</Button
          >
        </div>
      </div>
    </template>
  </ModalDialog>
</template>

<script setup lang="ts">
import ModalDialog from "@/shared/ui/modal/ModalDialog.vue";
import { DropDownList } from "@progress/kendo-vue-dropdowns";
import {
  getLabelTypePrintOption,
  printLocAsync,
} from "@/shared/helpers/printLocHelper";
import {
  onBeforeMount,
  ref,
  watchEffect,
  watch,
  type Ref,
  computed,
} from "vue";
import {
  fileNameBuilderOptions,
  MaxNumberOfSingleLocsForImmediateDownload,
  type ILabelTypePrintOptions,
  type ILocHeaderOption,
  type IOption,
  type IPrintOption,
} from "@/Domain/PrintLocModalOptions";
import { useMainStore } from "@/stores/useMainStore";
import type { IdAndName } from "@/Domain/IdAndName";
import type { CompanySummary } from "@/Domain/CompanySummary";
import { EnumPrintLocType } from "@/Domain/EnumPrintLocType";
import { Button } from "@progress/kendo-vue-buttons";
import { api } from "@/api";
import { CacheService } from "@/shared/services/CacheService";
import { createCacheKey } from "@/shared/helpers/cacheHelper";
import { handleResponsePromiseAsync } from "@/shared/helpers/apiHelper";
import LoadingButton from "@/shared/ui/inputs/LoadingButton.vue";
import { isValidFilename } from "@/shared/helpers/fileHelper";

interface ILinkLocHeaderOption extends ILocHeaderOption {
  downloading?: boolean;
}

const props = defineProps<{
  open: boolean;
  labelType: EnumPrintLocType;
  plantList: IdAndName[];
  selectedProductIds: string[];
  companySummary: CompanySummary;
  totalProducts: number;
  showNotice?: boolean;
}>();

const emit = defineEmits(["closeModal"]);

const main = useMainStore();
const options = ref<ILabelTypePrintOptions | undefined>(undefined);
const selectedHeader = ref<IOption>({} as IOption);
const selectedSortOrder = ref<IOption>({} as IOption);
const selectedPrintOption = ref<IPrintOption>({} as IPrintOption);
const fileName = ref<string>("LOC");
const folderName = ref<string>("");
const plantList = ref<IdAndName[]>([]);
const selectedProductIds = ref<string[]>([]);
const pickupNoticeShown = ref<boolean>(props.showNotice || false);
const setDefaultOptionChecked = ref<boolean>(false);
const locPrinting = ref<boolean>(false);

const preferenceCacheKey = "PrintLocPreference";

const invalidCustomNameText =
  "Provided file name is invalid. Remove special characters and try again.";

const isCustomNameValid = computed(() => {
  return isValidFilename(
    fileName.value.replaceAll("{Date/Time}", "{Date-Time}")
  );
});

const getPreferenceRequestParams = function (propertyName: string) {
  return {
    userLogin: main.userLogin,
    companyLogin: main.companyLogin,
    propertyName: propertyName,
  };
};

const getPreferenceAsync = async function (propertyName: string) {
  const requestParams = getPreferenceRequestParams(propertyName);

  return CacheService.getResponseAsync(
    createCacheKey(requestParams, preferenceCacheKey),
    () => api.get("GetPropertyValues", requestParams)
  );
};

const setPreferenceAsync = async function (
  propertyName: string,
  propertyValue: string
) {
  console.debug(
    "PrintLocModal.vue, setPreferenceAsync, propertyName, propertyValue",
    propertyName,
    propertyValue
  );
  const requestParams = {
    userLogin: main.userLogin,
    companyLogin: main.companyLogin,
    propertyName: propertyName,
    propertyValue: propertyValue,
  };
  const cacheParams = getPreferenceRequestParams(propertyName);

  await api.get("SetPropertyValues", requestParams);
  CacheService.remove(createCacheKey(cacheParams, preferenceCacheKey));
};

const updateSelectedOptionAsync = async function (
  selectedOptionRef: Ref<IOption> | Ref<string>,
  options: IOption[],
  preference?: string
) {
  let selectedOptionSet = false;
  try {
    if (!preference) return;

    const response = await getPreferenceAsync(preference);
    if (!response.success) {
      console.error(
        "PrintLocModal.vue, updateSelectedOptionAsync, error getting preference",
        response
      );
      return;
    }

    const preferenceValue = response.response[0];

    if (!preferenceValue) return;

    if (
      preference.endsWith("NameBuilderPreference") ||
      selectedOptionRef.value === "string"
    ) {
      selectedOptionRef.value = preferenceValue;
    } else {
      const option = options.find((o) => o.value === preferenceValue);
      if (!option) return;
      selectedOptionRef.value = option;
    }
    selectedOptionSet = true;
    console.debug(
      "PrintLocModal.vue, updateSelectedOptionAsync, preference",
      preference,
      "value",
      preferenceValue
    );
  } catch (e) {
    console.error(
      "PrintLocModal.vue, updateSelectedOptionAsync, error getting preference",
      e
    );
  } finally {
    if (!selectedOptionSet && options.length > 0)
      selectedOptionRef.value = options[0];
  }
};

const updateOptionsAsync = async function () {
  options.value = getLabelTypePrintOption(
    Object.keys(EnumPrintLocType)[
      Object.values(EnumPrintLocType).indexOf(props.labelType)
    ],
    props.companySummary
  );

  await updateSelectedOptionAsync(
    selectedHeader,
    options.value!.letterHeaderOptions,
    options.value?.letterHeaderPreference
  );

  if (options.value?.isLinkOption) return;
  if (options.value?.sortOrderOptions)
    await updateSelectedOptionAsync(
      selectedSortOrder,
      options.value.sortOrderOptions,
      options.value?.sortOrderPreference
    );
  if (options.value?.printOptions)
    await updateSelectedOptionAsync(
      selectedPrintOption,
      options.value.printOptions,
      options.value?.printOptionPreference
    );

  await updateSelectedOptionAsync(
    fileName,
    [],
    options.value?.nameBuilderPreference
  );
  console.debug(
    "PrintLocModal.vue, updateOptionsAsync, namebuilderoptions fileName = " +
      fileName.value,
    options.value
  );
};

const runMethodNameValuePairsAsync = async function () {
  let UniqueCustomFolderName = "";
  if (folderName.value > "") {
    UniqueCustomFolderName =
      folderName.value.trim() +
      "UniqueIdTag" +
      new Date()
        .toISOString()
        .replaceAll(":", "")
        .replaceAll(" ", "")
        .replaceAll("-", "")
        .replaceAll(".", "");
    console.debug("CustomFolderName: " + UniqueCustomFolderName);
  } else {
    UniqueCustomFolderName =
      "LOCUniqueIdTag" +
      new Date()
        .toISOString()
        .replaceAll(":", "")
        .replaceAll(" ", "")
        .replaceAll("-", "")
        .replaceAll(".", "");
    console.debug("UniqueFolderName: " + UniqueCustomFolderName);
  }
  let UserLoginForLocPickup = "";
  if (main.isEmulator)
    UserLoginForLocPickup = main.userLogin + "." + main.companyLogin;
  else UserLoginForLocPickup = main.userLogin;

  return api.post("GenerateLocsForPickup", {
    UserLoginForLocPickup: UserLoginForLocPickup,
    CompanyLogin: main.companyLogin,
    HeaderOption: selectedHeader.value?.value || "",
    NameBuilder: fileName.value,
    PlantList: plantList.value.map((p) => p.ID).join(";"),
    SelectedIds: selectedProductIds.value.join(";"),
    LabelType:
      Object.keys(EnumPrintLocType)[
        Object.values(EnumPrintLocType).indexOf(props.labelType)
      ],
    CustomZipFileName: UniqueCustomFolderName,
    PrintOption: selectedPrintOption?.value.value,
    ProductByBrand: selectedSortOrder?.value.value == "true",
  });
};

const generateLocsForPickupAsync = async function () {
  try {
    const result = await runMethodNameValuePairsAsync();
    if (!result.success) {
      console.error(
        "PrintLocModal.vue, generateLocsForPickupAsync, result",
        result
      );
      return;
    }

    console.debug(
      "PrintLocModal.vue, generateLocsForPickupAsync, result",
      result
    );
  } catch (error) {
    console.error(
      "PrintLocModal.vue, generateLocsForPickupAsync, error",
      error
    );
  }
};

const closeModal = function () {
  pickupNoticeShown.value = false;
  emit("closeModal");
};

const fileNameLabelClicked = function (text: string) {
  fileName.value = fileName.value + text;
  console.debug(
    "PrintLocModal.vue, folderNameLabelClicked, folderName",
    fileName.value
  );
};

const downloadClicked = async function () {
  console.debug("PrintLocModal.vue, downloadClicked");
  if (locPrinting.value) return;

  locPrinting.value = true;
  const productCount = selectedProductIds.value.length || props.totalProducts;

  if (productCount > MaxNumberOfSingleLocsForImmediateDownload) {
    pickupNoticeShown.value = true;
    generateLocsForPickupAsync();
  } else {
    let UniqueCustomFolderName = "";
    if (folderName.value > "") {
      UniqueCustomFolderName = folderName.value
        .trim()
        .replaceAll("<", "")
        .replaceAll(">", "");
      console.debug("CustomFolderName: " + UniqueCustomFolderName);
    }

    await printLocAsync({
      CompanyLogin: main.companyLogin,
      LocCalledFrom: "Vue",
      LocReportMyCompId: main.companyLogin,
      LocReportPrintOption: selectedPrintOption?.value.value,
      LocReportIsFullLoc: true,
      LocReportLabelType:
        Object.keys(EnumPrintLocType)[
          Object.values(EnumPrintLocType).indexOf(props.labelType)
        ],
      LocReportHeaderOption: selectedHeader?.value.value,
      LocReportSelectedIds: selectedProductIds.value.join(";"),
      LocReportPlantList: plantList.value.map((p) => p.ID).join(";"),
      LocReportIndividualPlant:
        selectedPrintOption?.value.individualPlant || false,
      LocReportDistList: "ALL",
      LocReportProductByBrand: selectedSortOrder?.value.value == "true",
      LocReportNameBuilder: selectedPrintOption.value?.showFileNameFormat
        ? fileName.value
        : "",
      LocReportCustomZipFileName: selectedPrintOption.value?.showFolderName
        ? UniqueCustomFolderName
        : "",
      LocReportCalledFromUkd: false,
    });
  }

  if (setDefaultOptionChecked.value) {
    console.debug(
      "PrintLocModal.vue: downloadClicked(), setDefaultOptionChecked is checked. options.value?.letterHeaderPreference = " +
        options.value?.letterHeaderPreference
    );

    if (options.value?.letterHeaderPreference)
      await setPreferenceAsync(
        options.value.letterHeaderPreference,
        selectedHeader.value.value
      );

    if (options.value?.sortOrderPreference)
      await setPreferenceAsync(
        options.value.sortOrderPreference,
        selectedSortOrder.value.value
      );

    if (options.value?.printOptionPreference)
      await setPreferenceAsync(
        options.value.printOptionPreference,
        selectedPrintOption.value.value
      );

    if (options.value?.nameBuilderPreference)
      await setPreferenceAsync(
        options.value.nameBuilderPreference,
        fileName.value
      );
  }

  locPrinting.value = false;
};

const linkOptionClicked = async function (headerOption: ILinkLocHeaderOption) {
  selectedHeader.value = headerOption;
  headerOption.downloading = true;
  await downloadClicked();
  headerOption.downloading = false;
};

const updatePrintOptions = function () {
  const labelTypeOptions = getLabelTypePrintOption(
    Object.keys(EnumPrintLocType)[
      Object.values(EnumPrintLocType).indexOf(props.labelType)
    ],
    props.companySummary
  );
  if (!labelTypeOptions || !options.value) return;

  options.value.printOptions = labelTypeOptions.printOptions?.filter(
    (opt) =>
      !opt.validateRestrictions ||
      opt.validateRestrictions(
        selectedHeader.value,
        selectedSortOrder.value,
        props.companySummary
      )
  );

  if (
    !options.value.printOptions ||
    options.value.printOptions?.some(
      (opt) => opt.value === selectedPrintOption.value.value
    )
  )
    return;

  selectedPrintOption.value = options.value.printOptions[0];
};

watch(
  () => selectedHeader.value,
  async () => {
    updatePrintOptions();
  }
);
watch(
  () => selectedSortOrder.value,
  async () => {
    updatePrintOptions();
  }
);
watch(
  () => props.labelType,
  async () => {
    await updateOptionsAsync();
  }
);
watchEffect(() => {
  plantList.value = props.plantList;
});
watchEffect(() => {
  selectedProductIds.value = props.selectedProductIds;
});

onBeforeMount(async () => {
  await updateOptionsAsync();
});
</script>

<style lang="scss">
.print-modal-content {
  padding: 30px 30px 40px;

  .modal-dropdown {
    margin-bottom: 16px;
  }

  .name-constructor {
    width: 100%;

    &-error {
      color: red;
      font-size: 12px;
    }
  }

  .name-constructor__builders {
    display: flex;
    flex-wrap: wrap;
    margin-top: 10px;

    .builder {
      display: inline-block;
      padding: 5px 10px;
      margin: 0 5px 5px 0;
      border: 1px solid #ccc;
      border-radius: 5px;
      cursor: pointer;
    }
  }

  .file-name-format-container,
  .folder-name-container {
    margin-bottom: 16px;

    input {
      width: 100%;
    }
  }

  .loc-form-container {
    margin-bottom: 16px;

    .loc-popup-list {
      list-style: none;
      padding: 0;
      margin: 0;

      .option-icon {
        color: #50a7d8;
        margin-right: 10px;
      }

      li {
        margin-bottom: 10px;
        min-width: unset !important;

        button {
          background: unset;
          border-color: unset;
          color: #399cd3;
          border-width: 0;

          &:hover {
            color: #0a58ca;
          }

          &:focus {
            box-shadow: unset;
          }
        }

        .loading-overlay {
          background-color: unset;
        }
      }
    }
  }

  .loc-pickup-notice {
    .k-button {
      display: inline-block;
      width: 100%;
      white-space: break-spaces;
    }
  }

  .print-option-checkbox {
    margin-left: 12px;
  }
}
</style>
